import React from "react";

// External
import styled, { css } from "styled-components";
import { StaticImage } from "gatsby-plugin-image";
import {
  FiFacebook,
  FiTwitter,
  FiLinkedin,
  FiLink,
  FiChevronLeft,
} from "react-icons/fi";
import { navigate } from "gatsby";

// Internal
import useMediaQuery from "../../hooks/use-media-query";
import IconButton from "../../components/form/IconButton";
import { isVideo } from "../../utils/helpers/file";
import { Flex } from "../../styles/global-styles";
import {
  fontFamily,
  colors,
  breakpoints,
  getProjectTheme,
  sectionRhythm,
} from "../../../theme";
import { safeNavigator } from "../../utils/helpers/ssr";
import { PROJECT_CONDEXO_PROPERTY_MANAGEMENT } from "../../utils/constants/projects";
import ContactMeSection from "../contact-me-section";
import { ProjectHero, caseStudyBodyStyles } from "./project-page-media";
import Testimonials from "../reviews-section/Testimonials";

const projectTheme = getProjectTheme("condexo-property-management");

// Styles

const hasClipboard = safeNavigator.clipboard !== undefined;

const BlogContainer = styled.div`
  width: calc(100vw - 48px);
  padding-top: ${sectionRhythm.paddingY.mobile}px;
  margin: 0 auto;

  img,
  video {
    width: 100%;
    height: auto;
  }

  @media only screen and (min-width: ${breakpoints.tablet}) {
    width: 65%;
    max-width: 800px;
    padding-top: ${sectionRhythm.paddingY.desktop}px;
    margin: 0 auto;
  }
  @media only screen and (min-width: 900px) {
    width: 60%;
  }
`;

const BlogTitle = styled.h1`
  margin: 0 0 16px;
  font-family: ${fontFamily.extraBold};
  font-size: 24px;
  // font-size: 28px;
  // width: 90%;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 44px;
    margin: 0 0 32px;
  }
`;
const BlogHelperText = styled.span`
  color: ${colors.grey.darkMedium};
  // margin-right: 12px;
`;
const Video = styled.video`
  //   width: 100%;
  height: 102%;
  border-radius: 6px !important;
  transform: scale(1.01);
  // box-shadow: 2px 2px 12px rgba(0, 0, 0, 0.1);
  @media only screen and (min-width: ${breakpoints.tablet}) {
    border-radius: 12px;
  }
`;

const VideoContainer = styled.div`
  width: ${(props) => (props.isMobileCover ? "96%" : "100%")};
  border: 4px solid white;
  border-radius: 6px !important;
  overflow: hidden;
  box-sizing: border-box;
  background-image: linear-gradient(180deg, ${colors.white}, ${colors.white});
  box-shadow: 17px 17px 48px 0 rgb(0 0 0 / 12%);
  margin-top: 1.5rem;
`;

const VideoContainerMobile = styled(VideoContainer)`
  z-index: 2;
  position: absolute;

  width: 70px;
  height: 135.516px;
  right: 0px;
  bottom: -20px;
  margin: 0;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    width: 120px;
    height: 248.203px;
    right: -15px;
    bottom: -30px;
  }
`;

const HeroCoverSection = styled.div`
  position: relative;
  margin-top: 1.5rem;
`;

const BlogContentContainer = styled.div`
  ${caseStudyBodyStyles}
`;
const Name = styled.span`
  color: ${colors.grey.darkMedium};
`;

const SubTitleContainer = styled(Flex)`
  padding: 0;
`;

const BlogFooter = styled.div``;
const ShareBlogFooter = styled(Flex)`
  border-bottom: 1px solid ${colors.grey.medium};
  padding: 20px 13px 8px;
  margin-top: 36px;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    padding: 30px 20px;
    margin-top: 100px;
  }
`;

const OverviewSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2rem;
  margin-bottom: 0;
`;

const OverviewItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  @media only screen and (min-width: 1000px) {
    flex-direction: row;
  }
`;

const OverviewItem = styled.div`
  margin-bottom: 1rem;

  ${(props) =>
    props.isSeventyPercent &&
    css`
      width: 100%;
      @media only screen and (min-width: 1000px) {
        width: 70%;
      }
    `}
  ${(props) =>
    props.isThirtyPercent &&
    css`
      width: 100%;
      @media only screen and (min-width: 1000px) {
        width: 30%;
      }
    `}
`;
const OverviewLabel = styled.div`
  font-size: 14px;
  color: ${colors.grey.darkMedium};
  margin-bottom: 4px;
`;
const OverviewValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;

  a,
  &[href] {
    color: inherit;
    word-break: break-all;

    &:hover {
      color: ${colors.linkHover.light};
      text-decoration: underline;
    }
  }
`;

const GalleryContentContainer = styled.div`
  margin-block: 2rem;
`;

const GalleryContentItem = styled.div`
  margin-block: 2rem;
  position: relative;
`;

const VideoCaption = styled.span`
  font-size: 0.8rem;
  text-align: center;
  width: 100%;
  display: inline-block;
  margin-top: 0.8rem;
`;

const CondexoPropertyManagementProjectSection = () => {
  const isMobile = !useMediaQuery(`(min-width: ${breakpoints.tablet} )`);

  const goBack = () => {
    navigate(-1);
  };

  const randomData = {
    date: new Date(),
    title:
      "Reducing landlord's burden of handling properties using property management web app",
    clientName: "Condexo",
    projectName: "Condexo Property Management",
    role: "React developer",
    year: "2019-2021",
    cover_src:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648039284/projects/condexo-property-management/condexo-property__dashboard_fso9cu.mp4",
    mobile_cover_src:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648039329/projects/condexo-property-management/condexo-property__dashboard--mobile_nz3wn6.mp4",
    clientCountry: "United Kingdom",
    servicesProvided: "Front-end development",
    industry: "Real estate",
    techStack: [
      "Javascript",
      "React",
      "Redux",
      "Redux Saga",
      "React Bootstrap",
      "Stripe",
    ],
    projectLink: "https://manager.condexo.co.uk/",
    clientReviews: [
      {
        name: "Elisa Fiorenzani",
        position: "UX/UI designer",
        company: "Condexo",
        profilePic: "elisa-condexo.jpeg",
        text: "It was a pleasure to work with Parmeet, an honest and reliable Dev!",
      },
    ],
  };

  const mediaLinks = {
    property:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648038639/projects/condexo-property-management/condexo-pay__property_kqopqo_itjn26.mp4",
    tenancy:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648038690/projects/condexo-property-management/condexo-pay__create-tenancy_jq9qvl_ahn2ls.mp4",
    calendar:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648039494/projects/condexo-property-management/condexo-property__calendar_mbspuo.mp4",
    documents:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648039583/projects/condexo-property-management/condexo-property__documents_k2yytp.mp4",
    accounts:
      "https://res.cloudinary.com/parmeetsinghasija/video/upload/v1648038682/projects/condexo-property-management/condexo-property__accounts_s542wo_owaoka.mp4",
  };

  return (
    <>
      <BlogContainer>
        {isMobile && (
          <Flex spaceBetween>
            <IconButton
              bgColor="#bbb"
              iconColor="#000"
              style={{ marginLeft: "-12px" }}
              onClick={goBack}
              ariaLabel="Go back"
            >
              <FiChevronLeft />
            </IconButton>
            {/* <SocialMediaButtons shareLinks={shareLinks} isMobile={true} /> */}
          </Flex>
        )}
        <BlogTitle>{randomData.title}</BlogTitle>
        <SubTitleContainer spaceBetween>
          <Flex>
            <Name>Role: {randomData.role}</Name>
          </Flex>
          {!isMobile && (
            <Flex>
              <BlogHelperText>Year: {randomData.year}</BlogHelperText>
            </Flex>
          )}
        </SubTitleContainer>
        <HeroCoverSection>
          <ProjectHero
            src={PROJECT_CONDEXO_PROPERTY_MANAGEMENT.media.hero}
            alt={randomData.title}
          />
        </HeroCoverSection>
        <OverviewSection>
          <OverviewItemContainer>
            <OverviewItem isSeventyPercent>
              <OverviewLabel>Client</OverviewLabel>
              <OverviewValue>{randomData.clientName}</OverviewValue>
            </OverviewItem>
            <OverviewItem isThirtyPercent>
              <OverviewLabel>Country</OverviewLabel>
              <OverviewValue>{randomData.clientCountry}</OverviewValue>
            </OverviewItem>
          </OverviewItemContainer>
          <OverviewItemContainer>
            <OverviewItem isSeventyPercent>
              <OverviewLabel>Services provided</OverviewLabel>
              <OverviewValue>{randomData.servicesProvided}</OverviewValue>
            </OverviewItem>
            <OverviewItem isThirtyPercent>
              <OverviewLabel>Industry</OverviewLabel>
              <OverviewValue>{randomData.industry}</OverviewValue>
            </OverviewItem>
          </OverviewItemContainer>
          {isMobile && (
            <OverviewItem isHundredPercent>
              <OverviewLabel>Year</OverviewLabel>
              <OverviewValue>{randomData.year}</OverviewValue>
            </OverviewItem>
          )}
          <OverviewItem isHundredPercent>
            <OverviewLabel>Tech stack</OverviewLabel>
            <OverviewValue>{randomData.techStack.join(", ")}</OverviewValue>
          </OverviewItem>
          {randomData.projectLink ? (
            <OverviewItem>
              <OverviewLabel>Project link</OverviewLabel>
              <OverviewValue>
                <a
                  href={randomData.projectLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {randomData.projectLink}
                </a>
              </OverviewValue>
            </OverviewItem>
          ) : null}
        </OverviewSection>
        <BlogContentContainer>
          <section>
            <h4>Challenge</h4>
            <p>
              Being a landlord is hard, especially when you have multiple
              properties to handle. All the necessary information is spread
              across ten different apps, like paperwork being stored in some
              cloud storage app, conversations with tenants and contractors
              present in some chat app, payments being in some bank app, rent
              reminders, and follow-ups being in some calendar app. Having to
              handle so many applications can take up so much of one's time and
              efforts. A solution was needed to reduce this clutter and bring
              all these tasks under one super application to satisfy all the
              needs of all stakeholders involved with a given property.
            </p>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.property} />
            </VideoContainer>
          </section>
          <section>
            <h4>Process</h4>
            <p>
              The project began with meetings with various stakeholders to get a
              better understanding of the project. The platform was supposed to
              allow 3 different types of users (Landlords, Tenants, and
              Contractors) to register, and the services were to be customized
              for every type of user to best suit their needs. After getting a
              better understanding of the project, we proceeded further to
              review the designs which were then created by the Client's
              In-house UI designer. After reviewing the designs a better visual
              idea of the project was there with us. In my team, we had a couple
              of React developers (including me), a couple of backend
              developers, and a project manager. We proceeded further with
              planning and selecting the most appropriate technology that could
              suit the requirements of the client. After a thorough round of
              discussion, for the front-end part, we decided to proceed with the
              react-boilerplate as a React starter because the project was full
              of features, so scalability was to be kept in mind. Stripe was the
              client's payment service of choice, which was integrated for all
              the payment operations of the web app. The project had
              requirements for various custom components which were to be built
              from scratch, like Calendar component, Chat component, and
              features like Push notifications, Rent payments, etc. Throughout
              the development process, Agile development was followed by having
              sprints of a couple of weeks and utilizing tools like Jira and
              Basecamp. On successful completion of development, the code was
              verified by the client's development team and after some suggested
              changes, the code was later pushed to production.
            </p>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.tenancy} />
            </VideoContainer>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.calendar} />
            </VideoContainer>
          </section>
          <section>
            <h4>Outcome</h4>
            <p>
              The MVP version of the website was released in May 2020. It helped
              in getting more investors on board for the project. Production
              release of Version 2.0 of the application was done on February
              2021. It had new features and a better user experience, making the
              platform even easier to use.
            </p>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.documents} />
            </VideoContainer>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.accounts} />
            </VideoContainer>
          </section>

          <section>
            <h4>Testimonials</h4>
            <Testimonials
              showMore={false}
              reviews={PROJECT_CONDEXO_PROPERTY_MANAGEMENT.client.reviews}
            />
          </section>
          {/*     <br />
        <br />
        <br />
        <br />
        <br />
        <h4>Gallery</h4>
        <GalleryContentContainer>
          <GalleryContentItem>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.giftCard} />
            </VideoContainer>
            <VideoCaption>My payments</VideoCaption>
          </GalleryContentItem>
          <GalleryContentItem>
            <VideoContainer>
              <Video autoPlay muted loop src={mediaLinks.giftCard} />
            </VideoContainer>
          </GalleryContentItem>
        </GalleryContentContainer> */}
        </BlogContentContainer>
      </BlogContainer>
      <ContactMeSection />
    </>
  );
};

export default CondexoPropertyManagementProjectSection;

/* DONE: Header Component */
/* TODO: Cover Image */
/* TODO: Project overview Section */
/* TODO: Challenge Section */
/* TODO: Process Section */
/* TODO: Outcome Section */
/* TODO: Client review Section */
/* TODO: Gallery Section */
/* TODO: Add link to right of project name */
/* TODO: Add icons for Role Section */
